import { Box } from "@mui/material";
import React, { useRef } from "react";

import { clampRatio } from "./splitRatio";

interface SplitHandleProps {
  /** The grid both panes live in; the ratio is measured against its width. */
  containerRef: React.RefObject<HTMLElement | null>;
  onChange: (ratio: number) => void;
  onCommit: (ratio: number) => void;
  onReset: () => void;
  hidden?: boolean;
}

/**
 * The divider between map and list. Pointer capture (as in the story-map
 * compare slider) keeps the drag alive when the pointer outruns the strip.
 */
export default function SplitHandle({ containerRef, onChange, onCommit, onReset, hidden }: SplitHandleProps) {
  const draggingRef = useRef(false);
  const lastRef = useRef<number | null>(null);

  const ratioFrom = (clientX: number): number | null => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    return clampRatio((clientX - rect.left) / rect.width);
  };

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the map and the list"
      onPointerDown={(e) => {
        draggingRef.current = true;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!draggingRef.current) return;
        const r = ratioFrom(e.clientX);
        if (r === null) return;
        lastRef.current = r;
        onChange(r);
      }}
      onPointerUp={() => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        if (lastRef.current !== null) onCommit(lastRef.current);
        lastRef.current = null;
      }}
      onDoubleClick={onReset}
      sx={{
        display: hidden ? "none" : "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "ew-resize",
        touchAction: "none",
        userSelect: "none",
        backgroundColor: "#e5e7eb",
        "&:hover > span, &:active > span": { backgroundColor: "#00d4ad" },
      }}
    >
      <Box component="span" sx={{ width: 4, height: 40, borderRadius: 2, backgroundColor: "#9ca3af" }} />
    </Box>
  );
}
